import React, { useEffect, useRef, useState } from 'react';
import { IDatasetSetpointConfig, IDatasetSetpointRowConfig } from './common/api/interfaces/interfaces';
import './DatasetSetpointTable.scss';
import DatasetSetpointRow from './DatasetSetpointRow';
import { ProgressSpinner } from 'primereact/progressspinner';
import { DatasetDataInfo, mapDatasetDataInfoResponseToDatasetDataInfo } from 'models/dataset-models';
import { Dialog } from 'primereact/dialog';
import DatasetLineChart from '../charts/dataset-line-chart/DatasetLineChart';
import { DatasetDataApi } from 'api/DatasetDataApi';
import { DatasetDataInfoResponse } from 'api/model/dataset/DatasetDataInfoResponse';

const DatasetSetpointTable = (props: IDatasetSetpointConfig) => {
    const [componentStart, setComponentStart] = useState<boolean>();

    const [data, setData] = useState<Array<IDatasetSetpointRowConfig>>();
    const [error, setError] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);

    const [configChecked, setConfigChecked] = useState<boolean>(false);

    const [dialog, setDialog] = useState<boolean>(false);
    const [selectedIdentifiers, setSelectedIdentifiers] = useState<Array<String>>([]);
    const [selectedIdentifiersDuplicate, setSelectedIdentifiersDuplicate] = useState<Array<String>>([]);
    const ref = useRef<HTMLDivElement>(null);
    useEffect(() => {
        if (!dialog) {
            if (selectedIdentifiers.length > 0) {
                setSelectedIdentifiers([]);
                setSelectedIdentifiersDuplicate([]);
            }
        }
    }, [dialog]);

    useEffect(() => {
        if (configChecked) {
            return;
        }

        setComponentStart(props.values && props.values.length > 0);

        if (Object.entries(props).length === 0) {
            setError('No config have been provided');
            setLoading(false);
        } else if (!props.values || props.values.length === 0) {
            setError('Value array is empty or field is not provided');
            setLoading(false);
        }

        setConfigChecked(true);
    }, [loading, props]);

    useEffect(() => {
        ref.current?.addEventListener('click', cancelSelections);

        return () => {
            ref.current?.removeEventListener('click', cancelSelections);
        };
    }, [ref]);

    useEffect(() => {
        let intervalId: any;
        if (componentStart) {
            concatArrays((tempArr) => {
                fetchData(tempArr);
                if (props.updateFrequency) {
                    intervalId = setInterval(() => fetchData(tempArr), props.updateFrequency * 1000);
                }
            });
        }
        return () => clearInterval(intervalId);
    }, [componentStart]); // eslint-disable-line

    const cancelSelections = (e: MouseEvent) => {
        if (e.ctrlKey || e.detail === 2) {
            return;
        }
        setSelectedIdentifiersDuplicate([]);
    };

    const openDialog = () => {
        if (!dialog) {
            setDialog(true);
        }
    };

    const concatArrays = (callback: (tempArr: Array<string>) => void) => {
        let tempArr: Array<string> = [];

        if (props.showSetpointColumn) {
            props.values.forEach((ob) => {
                if (ob.dataset) {
                    tempArr.push(ob.dataset);
                }
                if (ob.setpointDataset) {
                    tempArr.push(ob.setpointDataset);
                }
            });
        } else {
            props.values.forEach((ob) => {
                if (ob.dataset) {
                    tempArr.push(ob.dataset);
                }
            });
        }
        callback(tempArr);
    };

    const fetchData = (arr: Array<string>) => {
        const uniqueArr = [...new Set(arr)]; // getRecentData breaks if non unique array is given
        DatasetDataApi.getRecentData(uniqueArr)
            .then(({ error, data }) => {
                if (loading) {
                    setLoading(false);
                }
                if (error) {
                    setError('No data');
                    return;
                } else if (data) {
                    if (data.length > 0) {
                        remakeObjectArray(data);
                    } else {
                    }
                }
            })
            .catch((err) => {
                console.error(err);
            });
    };

    const remakeObjectArray = (res: Array<DatasetDataInfoResponse>) => {
        let tempArr: Array<IDatasetSetpointRowConfig> = [];
        let tempValues = [...props.values];

        tempValues.forEach((ob) => {
            let tempObj: IDatasetSetpointRowConfig = {
                tag: ob.tag,
                name: ob.name,
                opened: false,
                setpointSelected: false,
                datasetSelected: false,
                setIdentifiers() {},
            };

            if (ob.dataset) {
                let tempDatasetObject = res.find((e) => e.identifier === ob.dataset);
                if (tempDatasetObject) {
                    tempObj.dataset = mapDatasetDataInfoResponseToDatasetDataInfo(tempDatasetObject);
                    tempObj.highlightColor = ob.highlightColor;
                }
            }
            if (ob.setpointDataset && props.showSetpointColumn) {
                let tempDatasetObject = res.find((e) => e.identifier === ob.setpointDataset);
                if (tempDatasetObject) {
                    tempObj.setpointDataset = mapDatasetDataInfoResponseToDatasetDataInfo(tempDatasetObject);
                    tempObj.validation = ob.validation;
                    tempObj.highlightColor = ob.highlightColor;
                }
            }
            tempObj.highlightColor = ob.highlightColor;
            tempArr.push(tempObj);
        });
        setData(tempArr);

        if (loading) {
            setLoading(false);
        }
    };

    const setIdentifier = (id: string, ctrlPressed: boolean, doubleClick: boolean) => {
        if (ctrlPressed && !doubleClick) {
            let tempArr = [...selectedIdentifiersDuplicate];
            if (!tempArr.includes(id)) {
                tempArr.push(id);
                setSelectedIdentifiersDuplicate(tempArr);
            }
        } else if (!ctrlPressed && doubleClick) {
            let tempArr = [];
            tempArr.push(id);
            setSelectedIdentifiers(tempArr);
            setSelectedIdentifiersDuplicate(tempArr);
            openDialog();
        } else if (ctrlPressed && doubleClick) {
            let tempArr = selectedIdentifiersDuplicate;
            if (!tempArr.includes(id)) {
                tempArr.push(id);
                setSelectedIdentifiersDuplicate(tempArr);
            }
            setSelectedIdentifiers(tempArr);
            openDialog();
        }
    };

    const printableCheck = (tag: string | undefined, name: string | undefined, dataset: DatasetDataInfo | undefined, setpointDataset: DatasetDataInfo | undefined) => {
        if (!name && !dataset) {
            if (!props.showSetpointColumn && !props.showTagColumn) {
                return false;
            } else if (!props.showSetpointColumn && props.showTagColumn && !tag) {
                return false;
            } else if (props.showSetpointColumn && !props.showTagColumn && !setpointDataset) {
                return false;
            }
        }
        return true;
    };

    return (
        <div ref={ref} className={'main-main-window'}>
            {loading ? (
                <ProgressSpinner
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        overflow: 'hidden',
                    }}
                />
            ) : error.length > 0 ? (
                <div className={'main-window'}>{error}</div>
            ) : props && props.values.length > 0 ? (
                <>
                    <Dialog
                        header='SCADA tags chart'
                        visible={dialog}
                        style={{ width: 'min(100vw, 1000px)', height: '500px' }}
                        modal={false}
                        onHide={() => {
                            setDialog(false);
                            setSelectedIdentifiers([]);
                            setSelectedIdentifiersDuplicate([]);
                        }}
                    >
                        <DatasetLineChart identifiers={selectedIdentifiers} showDateSelection={true} dynamicHeight={true} chartDialogOpen={dialog} />
                    </Dialog>

                    <table className={'main-window'}>
                        <tbody>
                            {props.showTableTitles && (
                                <tr id='table-head'>
                                    {props.showTagColumn === true && <th>{props.tableTitles?.tagTitle}</th>}

                                    <th>{props.tableTitles?.datasetTitle}</th>

                                    <th>{props.tableTitles?.datasetValueTitle}</th>

                                    {props.showSetpointColumn === true && <th>{props.tableTitles?.setpointValueTitle}</th>}
                                </tr>
                            )}
                            {data?.map((value: IDatasetSetpointRowConfig, index: number, array: IDatasetSetpointRowConfig[] ) => {
                                const nameBeforeWasEmpty = index > 1 && value.name ? array[index - 1]?.name.length === 1 : false;
                                if (printableCheck(value.tag, value.name, value.dataset, value.setpointDataset)) {
                                    return (
                                        <DatasetSetpointRow
                                            showSetpointColumn={props.showSetpointColumn}
                                            showTagColumn={props.showTagColumn}
                                            compactMode={props.compactMode ? props.compactMode : false}
                                            tag={value.tag}
                                            name={value.name}
                                            nameBeforeWasEmpty={nameBeforeWasEmpty}
                                            setpointDataset={value.setpointDataset}
                                            dataset={value.dataset}
                                            datasetSelected={selectedIdentifiersDuplicate.includes(value.dataset ? value.dataset.identifier : '')}
                                            setpointSelected={selectedIdentifiersDuplicate.includes(value.setpointDataset ? value.setpointDataset.identifier : '')}
                                            opened={dialog}
                                            setIdentifiers={setIdentifier}
                                            validation={value.validation}
                                            highlightColor={value.highlightColor}
                                            key={index}
                                        />
                                    );
                                }
                            })}
                        </tbody>
                    </table>
                </>
            ) : (
                <></>
            )}
        </div>
    );
};

export default DatasetSetpointTable;
